import { useTranslation } from "react-i18next"

import { useApolloClient } from "@apollo/client"

import { ensureArray } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApiResponse } from "shared/types/ApiResponse"

import { useChangeProjectOwner } from "entities/projects/api/useChangeProjectOwner"
import { readProjectFragment } from "entities/projects/lib/readProjectFragment"
import { useCreateTeamMember } from "entities/team-members/api/useCreateTeamMember"
import {
  CreateTeamMemberInput,
  CreateTeamMemberInputSchema
} from "entities/team-members/types/CreateTeamMemberInput"
import { TeamMember } from "entities/team-members/types/TeamMember"
import { useTeamMutations } from "entities/teams/model/useTeamMutations"
import { Team } from "entities/teams/types/Team"
import z from "zod"

export function useCreateProjectTeam() {
  const { cache } = useApolloClient()
  const { t } = useTranslation()
  const { createTeam } = useTeamMutations()
  const { changeProjectOwner } = useChangeProjectOwner()
  const { createMultipleTeamMembers } = useCreateTeamMember()

  const { raiseLoadingToast: raiseCreateProjectTeamLoadingToast } =
    useRequestToasts({
      messages: {
        success: t("Request.Team.CREATE_PROJECT_TEAM.Success"),
        failure: t("Request.Team.CREATE_PROJECT_TEAM.Failure"),
        loading: t("Request.Team.CREATE_PROJECT_TEAM.Start")
      }
    })

  const createProjectTeam = useEvent(
    async ({
      projectID,
      teamMembers
    }: {
      projectID: string
      teamMembers:
        | Partial<CreateTeamMemberInput>
        | Partial<CreateTeamMemberInput>[]
    }): Promise<ApiResponse<Team, { memberships: TeamMember[] }>> => {
      const project = readProjectFragment(projectID, cache)
      const sendableInvites = ensureArray(teamMembers).filter(
        member => z.safeParse(CreateTeamMemberInputSchema, member).success
      ) as CreateTeamMemberInput[]

      const { raiseSuccessToast, raiseErrorToast } =
        raiseCreateProjectTeamLoadingToast()

      const team = (
        await createTeam(
          {
            name: t("Project.ProjectTeamName", {
              projectName: project?.title
            })
          },
          { suppressToasts: true }
        )
      ).data

      if (!team || !team?.id) {
        raiseErrorToast()
        return {
          data: null,
          errors: [{ message: t("Request.Team.CREATE_PROJECT_TEAM.Failure") }]
        }
      }

      const updatedProjectData = await changeProjectOwner(
        {
          projectID,
          teamID: team.id
        },
        { suppressToasts: true }
      )

      if (
        !updatedProjectData ||
        updatedProjectData.data?.project.team?.id !== team.id
      ) {
        raiseErrorToast()
        return {
          data: null,
          errors: [{ message: t("Request.Team.CREATE_PROJECT_TEAM.Failure") }]
        }
      }

      const createTeamMembersResponse = await createMultipleTeamMembers(
        {
          teamID: team.id,
          teamMembers: sendableInvites
        },
        { suppressToasts: true }
      )

      if (!createTeamMembersResponse.errors?.length) {
        raiseSuccessToast()
        return {
          data: team,
          meta: {
            memberships: createTeamMembersResponse.data || []
          }
        }
      }

      raiseErrorToast()
      return {
        data: null,
        errors: createTeamMembersResponse.errors || [
          { message: t("Request.Team.CREATE_PROJECT_TEAM.Failure") }
        ]
      }
    }
  )

  return { createProjectTeam }
}
